import React, { useEffect, useState } from "react";
import { TabsBase } from "@reusejs/react";
import { getBidderByUserUuid, getAuctionEventsForBidder } from "services";
import AuctionEventCard from "./BidderEvent";
import { useSelector } from "react-redux";
import Pusher from "pusher-js";

export default function ViewEvents() {
  const [events, setEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [liveEvents, setLiveEvents] = useState([]);
  const [cancelledEvents, setCancelledEvents] = useState([]);

  const user = useSelector((state: any) => {
    return state.bidderAuth.user;
  });

  useEffect(() => {
    if (user) {
      init(user.uuid);
    }
  }, [user]);

  const init = async (uuid: string) => {
    const eligibleBidder: any = await getBidderByUserUuid(uuid);
    const eventUuidsForBidder = eligibleBidder.map(
      (item: any) => item.event_uuid
    );
    // we can filter the below data so that we dont have to make many api calls
    const allEvents: any = await getAuctionEventsForBidder(
      eventUuidsForBidder,
      ["pending", "accepted"]
    );
    const allPastEvents: any = await getAuctionEventsForBidder(
      eventUuidsForBidder,
      ["end"]
    );
    const allLiveEvents: any = await getAuctionEventsForBidder(
      eventUuidsForBidder,
      ["live"]
    );

    const allCancelledEvents: any = await getAuctionEventsForBidder(
      eventUuidsForBidder,
      ["cancelled"]
    );
    setEvents(allEvents);
    setPastEvents(allPastEvents);
    setLiveEvents(allLiveEvents);
    setCancelledEvents(allCancelledEvents);
  };

  useEffect(() => {
    Pusher.logToConsole = false;
    const pusher = new Pusher(`${process.env.NEXT_PUBLIC_PUSHER_KEY}`, {
      cluster: process.env.NEXT_PUBLIC_PUSHER_CLUSTER,
    });

    const channelId = `update-dashboard`;
    const channel = pusher.subscribe(channelId);

    channel.bind("auction_event_has_started", (data: any) => {
      if (data) {
        if (user) {
          init(user.uuid);
        }
      }
    });

    channel.bind("auction_event_has_ended", (data: any) => {
      if (data) {
        if (user) {
          init(user.uuid);
        }
      }
    });

    pusher.connection.bind("error", function (err: any) {
      if (err?.error?.data?.code === 4004) {
        console.log(">>> detected limit error");
      } else if (err?.data?.code === 1006) {
        // Websocket: CLOSED ABNORMALLY
      }
    });

    pusher.connection.bind("disconnected", function (err: any) {
      console.log(">>> disconnected");
    });

    pusher.connection.bind("connected", function (err: any) {
      console.log("connected", pusher.sessionID);
    });

    return function cleanup() {
      pusher.disconnect();
    };
  }, [user]);

  return (
    <div className="w-full p-4 sm:p-6 flex flex-col justify-center">
      <div className="min-w-full flex justify-between">
        <div className="text-4xl text-blue-600 font-extrabold dark:text-red-600">
          Auction Events
        </div>
      </div>
      <TabsBase
        elements={[
          {
            title: (
              <div className="flex items-center px-2">Upcoming Events</div>
            ),
            content: (
              <div className="flex w-full justify-center border-2 border-t-0 rounded-b-lg bg-gray-50 border-gray-200">
                <div className="w-full p-4 sm:p-6 mt-4 space-y-6">
                  {events.length > 0 ? (
                    events.map((item: any, index: number) => {
                      return (
                        <AuctionEventCard
                          key={index}
                          property={item.property_dump}
                          event={item}
                          type="bidderEvents"
                        />
                      );
                    })
                  ) : (
                    <div className="w-full flex justify-center">No Events</div>
                  )}
                </div>
              </div>
            ),
          },
          {
            title: <div className="flex items-center px-2">Live Events</div>,
            content: (
              <div className="flex w-full justify-center border-2 border-t-0 rounded-b-lg bg-gray-50 border-gray-200">
                <div className="w-full p-4 sm:p-6 mt-4 space-y-6">
                  {liveEvents.length > 0 ? (
                    liveEvents.map((item: any, index: number) => {
                      return (
                        <AuctionEventCard
                          key={index}
                          property={item.property}
                          event={item}
                          type="bidderEvents"
                        />
                      );
                    })
                  ) : (
                    <div className="w-full flex justify-center">No Events</div>
                  )}
                </div>
              </div>
            ),
          },
          {
            title: <div className="flex items-center px-2">Past Events</div>,
            content: (
              <div className="flex w-full justify-center border-2 border-t-0 rounded-b-lg bg-gray-50 border-gray-200">
                <div className="w-full p-4 sm:p-6 mt-4 space-y-6">
                  {pastEvents.length > 0 ? (
                    pastEvents.map((item: any, index: number) => {
                      return (
                        <AuctionEventCard
                          key={index}
                          property={item.property}
                          event={item}
                          type="bidderEvents"
                        />
                      );
                    })
                  ) : (
                    <div className="w-full flex justify-center">No Events</div>
                  )}
                </div>
              </div>
            ),
          },
          {
            title: (
              <div className="flex items-center px-2">Cancelled Events</div>
            ),
            content: (
              <div className="flex w-full justify-center border-2 border-t-0 rounded-b-lg bg-gray-50 border-gray-200">
                <div className="w-full p-4 sm:p-6 mt-4 space-y-6">
                  {cancelledEvents.length > 0 ? (
                    cancelledEvents.map((item: any, index: number) => {
                      return (
                        <AuctionEventCard
                          key={index}
                          property={item.property}
                          event={item}
                          type="bidderEvents"
                        />
                      );
                    })
                  ) : (
                    <div className="w-full flex justify-center">No Events</div>
                  )}
                </div>
              </div>
            ),
          },
        ]}
        tabsBaseClasses={{
          wrapperClasses: "pt-16",
          layout: "flex items-center justify-center",
          height: "h-full",
          width: "w-full min-w-fit",
          backgroundColor: "bg-white",
          padding: "px-2",
          cursor: "cursor-pointer",
          borderRadius: "rounded-t-lg",
          border: "border-2 border-gray-200",
          activeTabClasses:
            "sticky left-0 right-0 bg-gray-50 border-b-0 scale-y-125 origin-bottom font-bold",
          inactiveTabClasses: "border-gray-200",
        }}
      />
    </div>
  );
}
